const typeOf = (group) => (group && group.type ? String(group.type).toUpperCase() : "");

const quantityOf = (modifier) => modifier.selectedQuantity || 0;

export const isArabic = () => {
   try {
      return localStorage.getItem("language") === "ar";
   } catch (e) {
      return false;
   }
};

export const localizedName = (entry, arabic = isArabic()) => {
   if (!entry) return "";
   if (arabic && entry.descriptionAlt) return entry.descriptionAlt;
   return entry.description || "";
};

export const hasModifierGroups = (item) =>
   !!(item && Array.isArray(item.modifierGroups) && item.modifierGroups.some(
      (group) => Array.isArray(group.modifiers) && group.modifiers.length > 0
   ));

export const isRadio = (group) => typeOf(group) === "RADIO";

export const showsCounter = (group, modifier) => {
   const type = typeOf(group);
   if (type === "COUNTER") return true;
   return type === "" && (modifier.priceInCents || 0) > 0;
};

export const selectedCount = (group) =>
   (group.modifiers || []).reduce((total, modifier) => total + quantityOf(modifier), 0);

export const canAddMore = (group) => {
   const maximum = group.maximumQuantity || 0;
   return maximum <= 0 || selectedCount(group) < maximum;
};

const withMinimumCounterDefaults = (group) => {
   const minimum = group.minimumQuantity || 0;
   const modifiers = group.modifiers || [];
   if (typeOf(group) !== "COUNTER" || minimum <= 0 || modifiers.length === 0) return group;
   if (modifiers.some((modifier) => quantityOf(modifier) > 0)) return group;
   const maximum = group.maximumQuantity || 0;
   const defaultQuantity = maximum > 0 ? Math.min(minimum, maximum) : minimum;
   const quantities = modifiers.map(() => 0);
   for (let step = 0; step < defaultQuantity; step++) {
      quantities[step % modifiers.length] += 1;
   }
   return {
      ...group,
      modifiers: modifiers.map((modifier, index) => ({ ...modifier, selectedQuantity: quantities[index] })),
   };
};

export const freshGroups = (groups) =>
   (groups || []).map((group) => withMinimumCounterDefaults({
      ...group,
      modifiers: (group.modifiers || []).map((modifier) => ({ ...modifier, selectedQuantity: 0 })),
   }));

const replaceModifier = (groups, groupIndex, modifierIndex, update) =>
   groups.map((group, g) => (g !== groupIndex ? group : update(group, modifierIndex)));

const setQuantity = (group, modifierIndex, quantity) => ({
   ...group,
   modifiers: group.modifiers.map((modifier, m) =>
      m === modifierIndex ? { ...modifier, selectedQuantity: quantity } : modifier
   ),
});

export const toggleModifier = (groups, groupIndex, modifierIndex) =>
   replaceModifier(groups, groupIndex, modifierIndex, (group, m) => {
      if (quantityOf(group.modifiers[m]) > 0) return setQuantity(group, m, 0);
      if (typeOf(group) === "RADIO") {
         return {
            ...group,
            modifiers: group.modifiers.map((modifier, i) => ({ ...modifier, selectedQuantity: i === m ? 1 : 0 })),
         };
      }
      if (!canAddMore(group)) return group;
      return setQuantity(group, m, 1);
   });

export const incrementModifier = (groups, groupIndex, modifierIndex) =>
   replaceModifier(groups, groupIndex, modifierIndex, (group, m) =>
      canAddMore(group) ? setQuantity(group, m, quantityOf(group.modifiers[m]) + 1) : group
   );

export const decrementModifier = (groups, groupIndex, modifierIndex) =>
   replaceModifier(groups, groupIndex, modifierIndex, (group, m) =>
      setQuantity(group, m, Math.max(quantityOf(group.modifiers[m]) - 1, 0))
   );

export const unmetGroupIndexes = (groups) =>
   (groups || []).reduce((unmet, group, index) => {
      const minimum = group.minimumQuantity || 0;
      if (minimum > 0 && selectedCount(group) < minimum) unmet.push(index);
      return unmet;
   }, []);

export const modifierPriceInCents = (groups) =>
   (groups || []).reduce(
      (total, group) => total + (group.modifiers || []).reduce(
         (sum, modifier) => sum + (modifier.priceInCents || 0) * quantityOf(modifier), 0
      ),
      0
   );

export const modifiersPayload = (groups) => {
   const payload = [];
   (groups || []).forEach((group) => {
      (group.modifiers || []).forEach((modifier) => {
         const quantity = quantityOf(modifier);
         if (quantity <= 0 || !modifier.id) return;
         payload.push({
            modifierItemId: modifier.id,
            quantity,
            description: modifier.description || "",
            descriptionAlt: modifier.descriptionAlt || "",
            priceInCents: modifier.priceInCents || 0,
            modifierType: typeOf(group),
         });
      });
   });
   return payload;
};

const payloadPriceInCents = (modifiers) =>
   (modifiers || []).reduce((total, modifier) => total + (modifier.priceInCents || 0) * (modifier.quantity || 0), 0);

export const lineUnitPriceInCents = (line) =>
   (line.priceInCents || 0)
   + payloadPriceInCents(line.modifiers)
   + (line.items || []).reduce((total, child) => total + payloadPriceInCents(child.modifiers), 0);

const modifiersKey = (modifiers) =>
   (modifiers || [])
      .map((modifier) => modifier.modifierItemId + "x" + modifier.quantity)
      .sort()
      .join(",");

export const lineKey = (line) =>
   [
      line.itemId,
      modifiersKey(line.modifiers),
      ...(line.items || []).map((child) => child.itemId + "[" + modifiersKey(child.modifiers) + "]"),
   ].join("|");

export const modifierLine = (modifier, arabic = isArabic()) => {
   const name = localizedName(modifier, arabic);
   const showQuantity = modifier.modifierType === "COUNTER"
      || (modifier.priceInCents || 0) > 0
      || (modifier.quantity || 0) > 1;
   return showQuantity ? Math.max(modifier.quantity || 0, 1) + " x " + name : name;
};

export const lineModifierSummary = (line, arabic = isArabic()) => {
   const names = (line.modifiers || []).map((modifier) => modifierLine(modifier, arabic));
   (line.items || []).forEach((child) => {
      (child.modifiers || []).forEach((modifier) => names.push(modifierLine(modifier, arabic)));
   });
   return names.join(", ");
};

export const minimumWarning = (group, arabic = isArabic()) => {
   const minimum = group.minimumQuantity || 0;
   return arabic ? "اختار " + minimum + " إضافات" : "You have to select minimum of " + minimum + " sides";
};

export const formatKwd = (cents) => "KWD " + ((cents || 0) / 100).toFixed(3);
